import { BUSINESS_TIME_ZONE } from "./format";
import type { InvoiceDetail, InvoiceLine } from "./services/invoicing";

/**
 * The tax invoice as display text, for `components/tax-invoice.tsx` — the print route's document.
 *
 * A copy of `steelyard-portal/lib/invoice-model.ts` (2026-10-05, owner decision: the admin's printed
 * invoice is the same document the customer prints and downloads). The two apps share no code, so the
 * copies are kept in step by hand: a change to one is made to the other. The only differences are
 * that this reads the admin's validated `InvoiceDetail` (`lib/services/invoicing.ts`) rather than the
 * raw generated type, and that there is no PDF download here, so no file-name helper.
 *
 * ## Nothing here is calculated (constitution I, `006` FR-011)
 *
 * Every figure is a field Billing sent — per-line taxable value, CGST, SGST, IGST and total, the
 * invoice's taxable value, tax heads, round-off and grand total — formatted, never summed,
 * multiplied or rounded here. The one thing derived is the amount in words, which spells out the
 * API's own `grandTotal`; that is a way of writing a number, not a tax computation.
 *
 * ## Nothing is invented either
 *
 * A field Billing did not send is left out, never filled with a dash or a placeholder (FR-012):
 * no IRN means no IRN block, no e-way bill means no e-way bill block, and a missing GSTIN says in
 * words that it is missing (FR-013). Bank details and terms are not part of `InvoicePrint`, so the
 * invoice carries neither until Billing records them.
 */
export type InvoicePrint = InvoiceDetail;
type InvoicePrintLine = InvoiceLine;

/** What the order behind the invoice says about payment — read separately, so optional. */
export type InvoicePayment = { orderType: string; paymentStatus: string };

export type InvoiceLineModel = {
  no: number;
  name: string;
  detail: string;
  hsn: string;
  quantity: string;
  unit: string;
  rate: string;
  /** D-39: this line carries a trade discount; gross and discount print only then. */
  discounted: boolean;
  gross: string;
  tradeDiscount: string;
  taxable: string;
  gstRate: string;
  cgst: string;
  sgst: string;
  igst: string;
  total: string;
};

export type InvoiceFact = { label: string; value: string };
export type InvoiceTotal = { label: string; value: string; strong?: boolean };

export type InvoiceModel = {
  invoiceNumber: string;
  financialYear: string;
  invoiceDate: string;
  orderNumber: string | null;
  cancelled: boolean;
  /** D-40: raised but held for Steelyard's review before it is final. */
  reviewPending: boolean;
  status: string;
  irnStatus: string;
  supplier: { name: string; address: string | null; gstin: string; gstinMissing: boolean; stateCode: string };
  recipient: { name: string; address: string; gstin: string; gstinMissing: boolean; placeOfSupply: string };
  /** "cgst_sgst" shows CGST and SGST, "igst" shows IGST, anything else shows all three. */
  taxHead: { label: string; showCgstSgst: boolean; showIgst: boolean };
  basis: { label: string; rule: string | null };
  discounted: boolean;
  lines: InvoiceLineModel[];
  totals: InvoiceTotal[];
  grandTotal: string;
  amountInWords: string | null;
  compliance: InvoiceFact[];
  irn: string | null;
  irnQr: string | null;
  payment: InvoiceFact[];
};

const MONEY = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" });

/** Indian digit grouping, display only — the API's figure, already rounded by Billing. */
export function money(value: number | null | undefined): string {
  return MONEY.format(value ?? 0);
}

function words(token: string): string {
  return token.replaceAll("_", " ");
}

function sentence(token: string): string {
  const text = words(token);
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** A plain ISO date (`2026-09-30`) as the day it names — never shifted by a time zone. */
export function invoiceDate(iso: string): string {
  return new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }).format(
    new Date(iso),
  );
}

/** An instant (e-way bill validity) in the business's clock, labelled "IST" in India — not the runtime's "GMT+5:30". */
function moment(iso: string): string {
  const date = new Date(iso);
  const time = new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: BUSINESS_TIME_ZONE,
  }).format(date);
  const zone = new Intl.DateTimeFormat("en-IN", { timeZone: BUSINESS_TIME_ZONE, timeZoneName: "short" })
    .formatToParts(date)
    .find((part) => part.type === "timeZoneName")?.value;
  return `${time} ${zone}`;
}

const ONES = [
  "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve",
  "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen",
];
const TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

/**
 * Words from digits, not from arithmetic: the figure is read as the string `Intl` prints for the
 * API's own two-decimal value, so nothing is rounded, floored or divided here (`no-credit-math`
 * guard; §8.5 — Billing's figures arrive already rounded).
 */
function belowHundred(digits: string): string {
  const n = Number(digits);
  if (n < 20) return ONES[n]!;
  const tens = Number(digits.slice(-2, -1));
  const unit = Number(digits.slice(-1));
  return TENS[tens]! + (unit ? ` ${ONES[unit]!}` : "");
}

function belowThousand(digits: string): string {
  const padded = digits.padStart(3, "0");
  const hundreds = Number(padded.slice(0, 1));
  const rest = padded.slice(1);
  return [hundreds ? `${ONES[hundreds]!} Hundred` : "", Number(rest) ? belowHundred(rest) : ""].filter(Boolean).join(" ");
}

/**
 * A whole number, as its digits, in the Indian system — the last three digits, then thousands and
 * lakhs in pairs, then crores (themselves spelled the same way): "141600" → "One Lakh Forty One
 * Thousand Six Hundred".
 */
function indianWords(digits: string): string {
  const whole = digits.replace(/^0+(?=\d)/, "");
  if (whole === "0") return "Zero";
  const rest = whole.slice(-3);
  const thousand = whole.slice(-5, -3);
  const lakh = whole.slice(-7, -5);
  const crore = whole.slice(0, -7);
  return [
    crore && Number(crore) ? `${indianWords(crore)} Crore` : "",
    lakh && Number(lakh) ? `${belowHundred(lakh)} Lakh` : "",
    thousand && Number(thousand) ? `${belowHundred(thousand)} Thousand` : "",
    Number(rest) ? belowThousand(rest) : "",
  ]
    .filter(Boolean)
    .join(" ");
}

const TWO_DECIMALS = new Intl.NumberFormat("en-IN", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  useGrouping: false,
});

/**
 * The grand total in words, as Indian invoices print it: "Indian Rupees One Lakh … and Fifty Paise
 * Only". Spells the API's own figure — rupees and paise read off its two decimals, not computed.
 * `null` for a figure that cannot be written that way (negative, or not a number).
 */
export function rupeesInWords(amount: number): string | null {
  if (!Number.isFinite(amount) || amount < 0) return null;
  const [rupees = "0", paise = "00"] = TWO_DECIMALS.format(amount).split(".");
  return `Indian Rupees ${indianWords(rupees)}${Number(paise) ? ` and ${belowHundred(paise)} Paise` : ""} Only`;
}

/**
 * GST state codes (the first two digits of a GSTIN) — fixed statutory reference data. The code is
 * what is stored and sent to NIC; only the name is ever shown ("24" → "Gujarat").
 */
const GST_STATES: Readonly<Record<string, string>> = {
  "01": "Jammu and Kashmir", "02": "Himachal Pradesh", "03": "Punjab", "04": "Chandigarh", "05": "Uttarakhand",
  "06": "Haryana", "07": "Delhi", "08": "Rajasthan", "09": "Uttar Pradesh", "10": "Bihar", "11": "Sikkim",
  "12": "Arunachal Pradesh", "13": "Nagaland", "14": "Manipur", "15": "Mizoram", "16": "Tripura", "17": "Meghalaya",
  "18": "Assam", "19": "West Bengal", "20": "Jharkhand", "21": "Odisha", "22": "Chhattisgarh", "23": "Madhya Pradesh",
  "24": "Gujarat", "26": "Dadra and Nagar Haveli and Daman and Diu", "27": "Maharashtra", "29": "Karnataka",
  "30": "Goa", "31": "Lakshadweep", "32": "Kerala", "33": "Tamil Nadu", "34": "Puducherry",
  "35": "Andaman and Nicobar Islands", "36": "Telangana", "37": "Andhra Pradesh", "38": "Ladakh",
  "97": "Other Territory", "99": "Centre Jurisdiction",
};

/** The State picker's list, by name — the same statutory data. 97 / 99 are GST bookkeeping codes, not places. */
export const ADDRESS_STATES: readonly { code: string; name: string }[] = Object.entries(GST_STATES)
  .filter(([code]) => Number(code) < 90)
  .map(([code, name]) => ({ code, name }))
  .sort((a, b) => a.name.localeCompare(b.name));


/** "24" → "Gujarat"; a value that is not a known code unchanged. For screens, where the name is what is read. */
export function stateName(value: string | null | undefined): string {
  const code = (value ?? "").trim();
  return GST_STATES[code] ?? code;
}

const BASIS_LABEL: Readonly<Record<string, string>> = {
  actual_measured_quantity: "Actual measured quantity at packing",
  actual_piece_count: "Actual piece count",
};

const BASIS_RULE: Readonly<Record<string, string>> = {
  actual_measured_quantity:
    "Raised on the quantity actually measured at packing, at the agreed unit rate. The quantity invoiced is not the quantity ordered.",
  actual_piece_count:
    "Raised on the actual piece count at the piece price. The weight recorded at packing is on the record and did not affect this amount.",
};

const TAX_HEAD: Readonly<Record<string, InvoiceModel["taxHead"]>> = {
  cgst_sgst: { label: "Intra-state supply — CGST + SGST", showCgstSgst: true, showIgst: false },
  igst: { label: "Inter-state supply — IGST", showCgstSgst: false, showIgst: true },
};

/** IRN states in the customer's words. An unlisted one prints as itself, never smoothed over. */
const IRN_WORDS: Readonly<Record<string, string>> = {
  not_applicable: "Not applicable",
  not_configured: "Pending",
  pending: "Pending",
  registered: "Registered",
  failed: "Registration failed",
  cancellation_requested: "Cancellation requested",
  cancelled: "Cancelled",
};

function hasDiscount(data: InvoicePrint): boolean {
  return (data.discount ?? 0) > 0;
}

function line(row: InvoicePrintLine, index: number): InvoiceLineModel {
  // Billing's percentage, printed beside the amount only when it is set (0 or absent: none).
  const percent = row.discountPercent;
  return {
    no: index + 1,
    name: row.skuName,
    detail: [row.brandName, row.categoryName].filter(Boolean).join(" · "),
    hsn: row.hsnCode,
    // Quantities at the precision the API sent them.
    quantity: String(row.quantity),
    unit: row.unitCode,
    rate: money(row.rate),
    discounted: (row.discount ?? 0) > 0,
    gross: money(row.grossAmount || row.taxableValue),
    tradeDiscount: percent ? `${money(row.discount)} (${String(percent)}%)` : money(row.discount),
    taxable: money(row.taxableValue),
    gstRate: `${String(row.gstRate)}%`,
    cgst: money(row.cgst),
    sgst: money(row.sgst),
    igst: money(row.igst),
    total: row.lineTotalDisplay,
  };
}

export function invoiceModel(data: InvoicePrint, payment?: InvoicePayment | null): InvoiceModel {
  const discounted = hasDiscount(data);
  const taxHead = TAX_HEAD[data.taxHead] ?? { label: sentence(data.taxHead), showCgstSgst: true, showIgst: true };

  const totals: InvoiceTotal[] = [
    ...(discounted
      ? [
          { label: "Gross value", value: money(data.grossValue || data.taxableValue) },
          { label: "Less trade discount", value: `− ${money(data.discount)}` },
        ]
      : []),
    { label: "Taxable value", value: money(data.taxableValue) },
    ...(taxHead.showCgstSgst
      ? [
          { label: "CGST", value: money(data.cgst) },
          { label: "SGST", value: money(data.sgst) },
        ]
      : []),
    ...(taxHead.showIgst ? [{ label: "IGST", value: money(data.igst) }] : []),
    // D-31: the order's delivery charge, invoiced untaxed — in the grand total, not the taxable value.
    ...((data.deliveryCharge ?? 0) > 0 ? [{ label: "Delivery charge (no GST)", value: money(data.deliveryCharge) }] : []),
    { label: "Round off", value: money(data.roundOff) },
  ];

  const compliance: InvoiceFact[] = [
    { label: "Invoice state", value: sentence(data.state) },
    { label: "e-Invoice (IRN)", value: IRN_WORDS[data.irnStatus] ?? sentence(data.irnStatus) },
    ...(data.ewayBillNo ? [{ label: "E-way bill no.", value: data.ewayBillNo }] : []),
    ...(data.ewayBillNo && data.ewayBillStatus ? [{ label: "E-way bill status", value: sentence(data.ewayBillStatus) }] : []),
    ...(data.ewayBillNo && data.ewayBillDate ? [{ label: "E-way bill date", value: moment(data.ewayBillDate) }] : []),
    ...(data.ewayBillNo && data.ewayBillValidUntil
      ? [{ label: "E-way bill valid until", value: moment(data.ewayBillValidUntil) }]
      : []),
  ];

  const paymentFacts: InvoiceFact[] = [
    ...(data.orderNumber ? [{ label: "Order", value: data.orderNumber }] : []),
    ...(payment ? [{ label: "Payment mode", value: sentence(payment.orderType) }] : []),
    ...(payment ? [{ label: "Payment status", value: sentence(payment.paymentStatus) }] : []),
  ];

  return {
    invoiceNumber: data.invoiceNumber,
    financialYear: data.financialYear,
    invoiceDate: invoiceDate(data.invoiceDate),
    orderNumber: data.orderNumber ?? null,
    cancelled: data.state === "cancelled",
    reviewPending: data.reviewPending ?? false,
    status: sentence(data.state),
    irnStatus: IRN_WORDS[data.irnStatus] ?? sentence(data.irnStatus),
    supplier: {
      name: data.supplierLegalName || "Infra Junction",
      address: data.supplierAddress || null,
      gstin: data.supplierGstin || "Not recorded — awaiting the company GSTIN",
      gstinMissing: !data.supplierGstin,
      stateCode: stateName(data.supplierStateCode),
    },
    recipient: {
      name: data.recipientLegalName,
      address: data.recipientAddress,
      gstin: data.recipientGstin || "None — unregistered recipient",
      gstinMissing: !data.recipientGstin,
      placeOfSupply: stateName(data.placeOfSupply),
    },
    taxHead,
    basis: {
      label: BASIS_LABEL[data.invoiceBasis] ?? sentence(data.invoiceBasis),
      rule: BASIS_RULE[data.invoiceBasis] ?? null,
    },
    discounted,
    lines: data.lines.map(line),
    totals,
    grandTotal: data.grandTotalDisplay,
    amountInWords: rupeesInWords(data.grandTotal),
    compliance,
    irn: data.irn || null,
    irnQr: data.irnQr || null,
    payment: paymentFacts,
  };
}

